import React from "react";

/**
 * PrivacyPolicy Component
 * Formatted structural layout for the Atomic Media Privacy Policy.
 */
const PrivacyPolicy = ({ isDarkMode }) => {
  return (
    <div className="space-y-6 text-left">
      
      {/* HEADER SECTION */}
      <div className="text-center border-b pb-4 border-zinc-200 dark:border-zinc-800">
        <h2 className="text-base font-black uppercase tracking-tight text-[#ff00cc]">
          Privacy Policy
        </h2>
        <p className="text-[10px] font-bold uppercase tracking-wider mt-1 opacity-60">
          Last Updated: May 2026
        </p>
        <p className="text-[10px] leading-relaxed mt-3 max-w-xl mx-auto italic">
          Atomic Media (ABN: 45 704 991 514) (“Atomic”, “we”, “us”, or “our”) is committed to protecting your privacy and handling your personal information in a secure, transparent manner. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website (atomicmusic.com.au), use our streaming applications, or interact with our services (collectively, the "Platform"). We manage your personal information in strict accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).
        </p>
      </div>

      {/* SECTION 1 */}
      <div>
        <h4 className="font-bold mb-1 text-[11px]">1. Information We Collect</h4>
        <p className="mb-2">
          To operate our direct-licensing music ecosystem, facilitate tipping, and track user-centric royalty payouts, we collect several categories of information:
        </p>
        <ul className="list-none pl-2 space-y-2">
          <li>
            <strong>• Account Information:</strong> Your legal name, stage/artist name, email address, password, profile picture, biography, and location details supplied upon registration.
          </li>
          <li>
            <strong>• Financial and Payout Metadata:</strong> If you are an Artist earning revenue on Atomic, we collect the banking details, Australian Business Number (ABN), or digital 'Statement by a Supplier' declarations required to facilitate secure payouts.
          </li>
          <li>
            <strong>• Listener Usage Data &amp; "Odo-Metrics":</strong> To execute our user-centric royalty distributions via our playhead tracking ratio, our system tracks and records real-time playback logs. This includes the precise duration (seconds streamed) of individual audio tracks, playlist builds, comments, and votes.
          </li>
          <li>
            <strong>• Technical and Cookies Data:</strong> IP addresses, browser types, device identifiers, operating systems, and page interaction metadata collected automatically via tracking cookies for performance, charting integrity, and anti-fraud purposes.
          </li>
        </ul>
      </div>

      {/* SECTION 2 */}
      <div>
        <h4 className="font-bold mb-1 text-[11px]">2. How We Use Your Information</h4>
        <p className="mb-2">
          We utilize the collected data to maintain a fair, functioning, and compliant music platform. Specific uses include:
        </p>
        <ul className="list-disc ml-4 space-y-1">
          <li>Operating, maintaining, and personalizing the streaming services, chart listings, and player interfaces.</li>
          <li>Calculating and allocating monthly subscription royalty distributions using our localized tracking metrics.</li>
          <li>Processing digital "Tips" and processing withdrawals net of our administrative platform fees.</li>
          <li>Verifying identity and tax status (e.g., storing ABNs or hobby declarations for 5 years as required by the Australian Taxation Office).</li>
          <li>Investigating, preventing, and stopping chart rigging, fraudulent playhead counts, card fraud, and cyber security breaches.</li>
          <li>Generating promotional, AI-assisted, or automated editorial summaries for artists in our Magazine/Feed sections.</li>
        </ul>
      </div>

      {/* SECTION 3 */}
      <div>
        <h4 className="font-bold mb-1 text-[11px]">3. Sharing and Disclosure of Information</h4>
        <p className="mb-2">
          We do not sell, rent, or trade your personal information to third parties for commercial marketing profit. We only disclose data in the following restricted scenarios:
        </p>
        <ul className="list-none pl-2 space-y-2">
          <li>
            <strong>• Third-Party Financial Integrations:</strong> We do not directly store credit card or financial credentials on our servers. All transactions (subscriptions, tips, and payouts) are safely routed through secure, PCI-compliant third-party processing gateways (such as Stripe or PayPal). You are subject to their respective privacy terms during transactions.
          </li>
          <li>
            <strong>• Public-Facing Platform Features:</strong> By design, artist profiles, track metrics, publicly posted comments, public playlists, and user-facing tip registries may be visible to other visitors on the Platform. Users may protect their physical identity by adopting pseudonyms or artist handles; registered email addresses and personal bank data always remain hidden from public view.
          </li>
          <li>
            <strong>• Legal &amp; Safe Harbour Obligations:</strong> We will disclose information if required to do so by law, court order, or by government regulators (such as the ATO), or if necessary to enforce our Terms of Service or protect the security and safety of our partnership, partners, and community.
          </li>
        </ul>
      </div>

      {/* SECTION 4 */}
      <div>
        <h4 className="font-bold mb-1 text-[11px]">4. Data Security and Retention</h4>
        <ul className="list-none pl-2 space-y-2">
          <li>
            <strong>• Security Measures:</strong> Atomic takes all reasonable physical, technological, and administrative steps to secure your personal information from unauthorized access, misuse, modification, or disclosure.
          </li>
          <li>
            <strong>• Tax Records:</strong> Financial records and tax compliance metadata (such as digital Statement by a Supplier forms) are held securely in restricted databases for a statutory period of five (5) years in compliance with Australian tax auditing mandates.
          </li>
          <li>
            <strong>• Data Breach Protocol:</strong> In the unlikely event of a data breach involving personal information that could result in serious harm, we will act in compliance with the Notifiable Data Breaches (NDB) scheme under the Privacy Act, notifying you and the Australian Information Commissioner expeditiously.
          </li>
        </ul>
      </div>

      {/* SECTION 5 */}
      <div>
        <h4 className="font-bold mb-1 text-[11px]">5. Your Rights: Access, Correction, and Deletion</h4>
        <p className="mb-2">
          You retain complete control over your personal data under the Australian Privacy Principles:
        </p>
        <ul className="list-none pl-2 space-y-2">
          <li>
            <strong>• Access &amp; Correction:</strong> You have the right to request a copy of the personal information we hold about you or request that we update incorrect metadata. Most account fields can be amended directly inside your user dashboard.
          </li>
          <li>
            <strong>• Account Deletion (The Right to Be Forgotten):</strong> You may request the absolute deletion of your profile by emailing our team. Upon processing, we will purge your personal metadata from our active servers, save for historical ledger transactions and tax declarations we are legally required to retain for regulatory archives.
          </li>
        </ul>
      </div>

      {/* SECTION 6 */}
      <div>
        <h4 className="font-bold mb-1 text-[11px]">6. Contact Us</h4>
        <p className="mb-2">
          If you have questions about this Privacy Policy, wish to exercise your data rights, or want to make a formal privacy complaint, please contact our partnership's privacy compliance officer:
        </p>
        
        <div className={`p-3 rounded-xl font-mono text-[9px] space-y-1 ${
          isDarkMode ? "bg-black/40 border border-zinc-800" : "bg-zinc-50 border border-zinc-200"
        }`}>
          <div><span className="opacity-50">ENTITY:</span> Atomic Media</div>
          <div><span className="opacity-50">EMAIL:</span> info@atomicmusic.space (Subject line: ATTN: PRIVACY COMPLIANCE)</div>
          <div><span className="opacity-50">ADDRESS:</span> PO Box 187 Jindera, NSW 2642</div>
        </div>

        <p className="mt-3 text-[10px] leading-relaxed">
          If you are unsatisfied with our internal response to a privacy complaint, you have the legal right to escalate the matter directly to the Office of the Australian Information Commissioner (OAIC) at{" "}
          <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer" className="text-[#ff00cc] underline hover:opacity-80">
            oaic.gov.au
          </a>.
        </p>
      </div>

      {/* FOOTER SIGN-OFF */}
      <div className="py-6 text-center font-black text-[#ff00cc] tracking-widest text-[10px]">
        --- END OF POLICY ---
      </div>
    </div>
  );
};

export default PrivacyPolicy;